import React from 'react';
import { Page, Document, StyleSheet, Text, View } from '@react-pdf/renderer';
import moment from 'moment';
import { mergeDuplicateProductLines, computeRoundedTotal } from '../Utils/helpers';

const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        padding: 20,
        width: '72mm', // Thermal printer width
        fontSize: 8
    },
    header: {
        textAlign: 'center',
        marginBottom: 10,
        fontWeight: 'bold'
    },
    address: {
        textAlign: 'center',
        marginBottom: 15,
    },
    tableHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontWeight: 'bold',
        paddingTop: 4,
        paddingBottom: 4
    },
    tableRow: {
        marginTop: 10,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 10,

    },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        fontWeight: 'bold',
        paddingTop: 4,
        paddingBottom: 4
    },
    viewBill: {
        flexDirection: 'row',
        paddingTop: 4,
        paddingBottom: 4,
        justifyContent: 'space-between'
    },
    dottedLine: {
        width: '100%',
        height: 0,
        borderTopWidth: 1,
        borderTopColor: '#000',
        borderTopStyle: 'dashed', // Creates the 1.5pt gap
    },
    table: {
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
    },
    row: {
        flexDirection: 'row',
        paddingVertical: 4,
    },
    tbl_header: {
        fontWeight: 'bold',
    },
    cell: {
        paddingHorizontal: 2,
        // Fixed widths for thermal printer (72mm = ~204pt)
        item: { width: 100 },    // 100pt for item column
        qty: { width: 32 },      // 52pt for quantity
        dis: { width: 30 },
        price: { width: 42 }     // 52pt for price (total 204pt)
    }
});

const BillReceipt = ({ invoiceData, customerDetails }: any) => {
    const rawItems = invoiceData || [];
    const items = mergeDuplicateProductLines(rawItems);
    // The billing-list row (`customerDetails`) doesn't carry `roundOff` — only
    // the invoice-detail rows do (ViewBillingSheet reads it the same way).
    const roundOff = rawItems[0]?.roundOff ?? customerDetails?.roundOff ?? 0;
    // Recomputed via the same shared round-off rule Create/Edit Bill and View
    // Billing use, rather than trusting the stored `totalamount` verbatim —
    // guarantees the printed TOTAL always matches those screens.
    const totalAmount = computeRoundedTotal(rawItems[0]?.amount, roundOff);
    // invoiceId/date: prefer the invoice-detail rows (fetched fresh for this exact
    // invoiceId) over customerDetails (found via billing.find() against the list,
    // which can be undefined/stale) so Bill No/Date don't show N/A when that lookup
    // misses.
    const invoiceId = rawItems[0]?.invoiceId ?? customerDetails?.invoiceId;
    const date = rawItems[0]?.date ?? customerDetails?.date;
    return (
        <Document>
            <Page size={[72 * 2.83465, 297 * 2.83465]} style={styles.page}>
                <Text style={styles.header}>MUKIZH FASHIONS</Text>
                <View style={styles.address}>
                    <Text >No.397/A, Station Road, Gnanamoorthy Nagar,</Text>
                    <Text>Ambattur, Chennai - 600 053</Text>
                    <Text>Mob No: 99402 37225 / 97902 95970</Text>
                </View>

                {/* Customer Info */}
                <View style={styles.dottedLine} />
                <View style={styles.viewBill}>
                    <Text>Bill No: {invoiceId ? invoiceId.replace(/INVCE-0+(\d+)/, 'INVCE$1') : 'N/A'}</Text>
                    <Text>Date: {date ? moment(date).format("DD-MMM-YYYY") : 'N/A'}</Text>
                </View>
                <View style={styles.dottedLine} />
                <View style={styles.table}>
                    {/* Header */}
                    <View style={[styles.row, styles.tbl_header]}>
                        <Text style={styles.cell.item}>ITEM</Text>
                        <Text style={[styles.cell.qty, { textAlign: 'right' }]}>QTY</Text>
                        <Text style={[styles.cell.dis, { textAlign: 'right' }]}>DIS</Text>
                        <Text style={[styles.cell.price, { textAlign: 'right' }]}>PRICE</Text>
                    </View>
                    <View style={styles.dottedLine} />
                    {/* Rows */}
                    {items.map((item: any, index: number) => {
                        const quantity = item?.itemsCount;
                        const mrp = parseFloat(item?.mrp || 0);
                        const discount = Number(item?.discountPercentage || 0);

                        const grossTotal = mrp * quantity;
                        const discountAmount = (discount / 100) * grossTotal;
                        const total = grossTotal - discountAmount;
                        return (
                            <View key={index?.toString()} style={styles.row}>
                                <Text style={styles.cell.item}>
                                    {(item?.productId || '').replace(/^ID-0+/, '')} - {item?.productName}
                                </Text>
                                <Text style={[styles.cell.qty, { textAlign: 'right' }]}>
                                    {item?.itemsCount}x{Number(item?.mrp)}
                                </Text>
                                <Text style={[styles.cell.qty, { textAlign: 'right' }]}>
                                    {discount > 0 ? `${discount.toFixed(2)}%` : "-"}
                                </Text>
                                <Text style={[styles.cell.price, { textAlign: 'right' }]}>
                                    {total.toFixed(2)}
                                </Text>
                            </View>
                        )
                    })}
                </View>
                {/*Sub Total & round Off */}
                <View style={styles.dottedLine} />
                <View style={styles.totalRow}>
                    <Text>SUB TOTAL</Text>
                    {/* Exact pre-roundoff amount (not `totalAmount + roundOff`, which would
                        show the rounded total's reconstruction and lose the exact paise) —
                        matches View Billing's "Total Amount" line. */}
                    <Text>{Number(rawItems[0]?.amount || 0).toFixed(2)}</Text>
                </View>
                <View style={styles.totalRow}>
                    <Text>Additional Discount</Text>
                    <Text>{Number(roundOff || 0).toFixed(2)}</Text>
                </View>
                <View style={styles.dottedLine} />
                <View style={styles.totalRow}>
                    <Text>TOTAL</Text>
                    <Text>{Number(totalAmount ?? 0).toFixed(2)}</Text>
                </View>
                <View style={styles.dottedLine} />
            </Page>
        </Document>
    );
};

export default BillReceipt;